import { useQuery } from "@tanstack/react-query";
import { getChemistriesByDateRange } from "../../repositories/ChemistriesReportRepository";

interface RawChemistryRow {
  ChemistrySampleId: number;
  HeatId: number;
  EAFProcessId: number | null;
  Date: string;
  Shift: string;
  Crew: string;
  Heat: string;
  Grade: string;
  Type: string;
  Reception: string;
  Reported: string;
  Technician: string;
  IsDuplicate: number;
  ElementName: string;
  Order: number;
  Value: number | null;
}

const processReportData = (rawRows: RawChemistryRow[]) => {
  if (!rawRows?.length) return { rows: [], elementNames: [] as string[] };

  const sampleMap = new Map<number, Record<string, unknown>>();
  const elementOrder = new Map<string, number>();

  for (const row of rawRows) {
    if (!sampleMap.has(row.ChemistrySampleId)) {
      sampleMap.set(row.ChemistrySampleId, {
        ChemistrySampleId: row.ChemistrySampleId,
        HeatId: row.HeatId,
        EAFProcessId: row.EAFProcessId,
        Date: row.Date,
        Shift: row.Shift,
        Crew: row.Crew,
        Heat: row.Heat,
        Grade: row.Grade,
        Type: row.Type,
        Reception: row.Reception,
        Reported: row.Reported,
        Technician: row.Technician,
        IsDuplicate: row.IsDuplicate,
      });
    }

    if (row.ElementName) {
      const sample = sampleMap.get(row.ChemistrySampleId)!;
      sample[row.ElementName] = row.Value;

      if (!elementOrder.has(row.ElementName)) {
        elementOrder.set(row.ElementName, row.Order ?? 999);
      }
    }
  }

  const elementNames = Array.from(elementOrder.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([name]) => name);

  return { rows: Array.from(sampleMap.values()), elementNames };
};

export const useChemistriesReportQuery = (
  startDate: Date,
  endDate: Date,
  sampleTypeId: number,
  gradeId: number,
) => {
  return useQuery({
    queryKey: [
      "chemistry-samples",
      startDate.toISOString(),
      endDate.toISOString(),
      sampleTypeId,
      gradeId,
    ],
    meta: { realtime: ["LAB.GetReportChemistrySamples"] },
    queryFn: async ({ signal }) => {
      const resp = await getChemistriesByDateRange(
        startDate,
        endDate,
        sampleTypeId,
        gradeId,
        signal,
      );
      if (resp.ok) {
        const rawRows = (resp.data?.tables?.[0]?.rows ??
          []) as RawChemistryRow[];
        return processReportData(rawRows);
      }
      throw new Error(resp.message);
    },
    // Polling for service-driven inserts (spectrometer data import)
    // SignalR handles user-driven updates from Lab Data Entry
    refetchInterval: 10_000,
  });
};
